import PropTypes from 'prop-types';
import { useMemo } from 'react';
// @mui
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  StyledEngineProvider,
  alpha,
  lighten,
  darken,
  emphasize,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
// hooks
import useSettings from '../hooks/useSettings';
//
import palette from './palette';
import typography from './typography';
import breakpoints from './breakpoints';
import componentsOverride from './overrides';
import shadows, { customShadows } from './shadows';

// -----------------------------------------------

ThemeProvider.propTypes = {
  children: PropTypes.node,
};

export default function ThemeProvider({ children }) {
  const { themeMode } = useSettings();
  const isLight = themeMode === 'light';

  const themeOptions = useMemo(
    () => ({
      palette: isLight ? palette.light : palette.dark,
      typography,
      breakpoints,
      shape: { borderRadius: 8 },
      direction: 'ltr',
      shadows: isLight ? shadows.light : shadows.dark,
      customShadows: isLight ? customShadows.light : customShadows.dark,
    }),
    [isLight]
  );

  const theme = createTheme(themeOptions);

  // ---------------------------------------------------------------------------
  // Compatibility shim for @material-table/core.
  //
  // The installed @material-table/core bundles its OWN copy of MUI v7
  // (node_modules/@material-table/core/node_modules/@mui/material@7.x), whose
  // components call color helpers AS THEME METHODS: `theme.alpha(...)`,
  // `theme.lighten(...)`, etc. This app runs MUI v5 (5.18.0), where those are
  // standalone imports and NOT theme methods, so the v7 components crash with
  // "theme.alpha is not a function".
  //
  // Attach them as plain, enumerable properties so they survive any theme
  // spread/clone that MUI v7 performs internally when reading the theme from
  // context. (A non-enumerable defineProperty is dropped by `{...theme}`.)
  theme.alpha = alpha;
  theme.lighten = lighten;
  theme.darken = darken;
  theme.emphasize = emphasize;

  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </StyledEngineProvider>
  );
}
